import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { fetchCollection } from '../lib/cms'
import { normalizePath, slugifyValue, stripHtml, truncateText } from '../lib/route'
import { designTokens } from '../data/siteData'

const SiteContentContext = createContext(null)

function getSlugFromEntry(entry, fallbackField = 'page_slug') {
  return (
    entry?.[fallbackField] ||
    entry?.slug?._ ||
    entry?.slug?.da ||
    entry?.news_slug ||
    entry?.title ||
    entry?.name ||
    ''
  )
}

function indexEntries(entries = [], fallbackField) {
  return entries.reduce((acc, entry) => {
    const keys = new Set()
    const primary = getSlugFromEntry(entry, fallbackField)
    if (primary) keys.add(normalizePath(`/${primary}`))
    if (entry?.page_slug) keys.add(normalizePath(`/${entry.page_slug}`))
    if (entry?.news_slug) keys.add(normalizePath(`/${entry.news_slug}`))
    if (entry?.name) keys.add(normalizePath(`/${slugifyValue(entry.name)}`))
    if (entry?.title) keys.add(normalizePath(`/${slugifyValue(entry.title)}`))

    for (const key of keys) {
      acc[key] = entry
    }

    return acc
  }, {})
}

function byDateDesc(a, b) {
  const aDate = new Date(a?.date || '1970-01-01').getTime()
  const bDate = new Date(b?.date || '1970-01-01').getTime()
  return bDate - aDate
}

function chooseImage(entry) {
  const image = entry?.image?.[0]
  return image || ''
}

function isItemActive(entry) {
  if (!entry) return false

  // Check taxonomy terms for status
  const statusTerm = entry?.terms?.['status']?.[0] || entry?.terms?.['status_tag']?.[0]
  if (statusTerm === 'deaktiveret') {
    return false
  }
  if (statusTerm === 'aktiv') {
    return true
  }

  // Fallback to old boolean field if present
  if (entry.is_active === false || entry.is_active === 'false' || entry.is_active === 0) {
    return false
  }

  return true
}

export function SiteContentProvider({ children }) {
  const [pages, setPages] = useState([])
  const [classes, setClasses] = useState([])
  const [news, setNews] = useState([])
  const [boardMembers, setBoardMembers] = useState([])
  const [calendarEvents, setCalendarEvents] = useState([])
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        setStatus('loading')
        const [pagesRes, classesRes, newsRes, boardRes, calendarRes] = await Promise.all([
          fetchCollection('pages'),
          fetchCollection('classes'),
          fetchCollection('news'),
          fetchCollection('board_members'),
          fetchCollection('calendar_events').catch(() => []),
        ])

        if (cancelled) return

        setPages(Array.isArray(pagesRes) ? pagesRes : [])
        setClasses(Array.isArray(classesRes) ? classesRes : [])
        setNews(Array.isArray(newsRes) ? newsRes : [])
        setBoardMembers(Array.isArray(boardRes) ? boardRes : [])
        setCalendarEvents(Array.isArray(calendarRes) ? calendarRes : [])
        setStatus('ready')
        setError('')
      } catch (err) {
        if (cancelled) return
        setStatus('error')
        setError(err?.message || 'Kunne ikke hente CMS-indhold.')
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [])

  const activePages = useMemo(() => pages.filter(isItemActive), [pages])
  const activeClasses = useMemo(() => classes.filter(isItemActive), [classes])
  const activeNews = useMemo(() => news.filter(isItemActive), [news])
  const activeCalendarEvents = useMemo(
    () =>
      [...calendarEvents]
        .filter(isItemActive)
        .sort((a, b) => (Number(a?.sort_order) || 99) - (Number(b?.sort_order) || 99)),
    [calendarEvents],
  )

  const pageIndex = useMemo(() => indexEntries(activePages, 'page_slug'), [activePages])
  const classIndex = useMemo(() => indexEntries(activeClasses, 'page_slug'), [activeClasses])
  const newsIndex = useMemo(() => indexEntries(activeNews, 'news_slug'), [activeNews])

  const latestNews = useMemo(() => [...activeNews].sort(byDateDesc), [activeNews])
  const featuredClasses = useMemo(() => activeClasses.slice(0, 8), [activeClasses])
  const boardSorted = useMemo(
    () => [...boardMembers].sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99)),
    [boardMembers],
  )

  const value = {
    status,
    error,
    pages: activePages,
    classes: activeClasses,
    news: activeNews,
    boardMembers: boardSorted,
    calendarEvents: activeCalendarEvents,
    pageIndex,
    classIndex,
    newsIndex,
    latestNews,
    featuredClasses,
    homeImage: designTokens.heroImage,
    findPage: (slug) => pageIndex[normalizePath(`/${slug}`)] || null,
    findClass: (slug) => classIndex[normalizePath(`/${slug}`)] || null,
    findNews: (slug) => newsIndex[normalizePath(`/${slug}`)] || null,
    pageFromPath: (pathname) => pageIndex[normalizePath(pathname)] || null,
    classFromPath: (pathname) => classIndex[normalizePath(pathname)] || null,
    newsFromPath: (pathname) => newsIndex[normalizePath(pathname)] || null,
    getPageSummary: (entry) => truncateText(stripHtml(entry?.content || ''), 180),
    getImage: chooseImage,
  }

  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>
}

export function useSiteContent() {
  const context = useContext(SiteContentContext)
  if (!context) {
    throw new Error('useSiteContent must be used within SiteContentProvider')
  }
  return context
}
